import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import DescriptionBox from "../../../components/descriptionBox/DescriptionBox";
import TlOnCameraSelect, { TlOnCameraValue } from "../../../components/tlOnCameraSelect/TlOnCameraSelect";
import { selectCurrentEmpid } from "../../../pages/login/authSlice";
import { useGetTriadCapabilityBucketQuery, useUpdateTriadMutation } from "../triad/triad.slice";

interface TriadThirdTabProps {
    setTriadPageNumber: React.Dispatch<React.SetStateAction<number>>;
    triadId: number | string;
    currTriad: any;
}

export const TriadThirdTabV2: React.FC<TriadThirdTabProps> = ({ setTriadPageNumber, triadId, currTriad }) => {

    const loggedEmpNum = useSelector(selectCurrentEmpid);
    const [UpdateTriad] = useUpdateTriadMutation();
    const {
        coaching_type: coachingType,
        observation_method: observationMethod,
        session_duration: sessionDuration,
        assessment_score: assessmentScore,
        employee_number: employeeNumber,
        author_employee_number: authorEmployeeNumber,
        top_strengths: topStrengths,
        primary_coaching_opportunity: primaryCoachingOpportunity,
        recommended_next_steps: recommendedNextSteps,
        acknowledged: initialAcknowledged,
        tl_on_camera_response: initialTlOnCameraResponse,
        month,
        year,
    } = currTriad ?? {};

    const isAuthor = authorEmployeeNumber === loggedEmpNum;
    const isCurrentMonthYear = month === new Date().getMonth() + 1 && year === new Date().getFullYear();
    const isEditable = isAuthor && !initialAcknowledged && isCurrentMonthYear;

    const containerRef = React.useRef<HTMLDivElement>(null);

    const { data: RatingBands } = useGetTriadCapabilityBucketQuery({});
    const matchedBucket = useMemo(() => {
        if (!RatingBands?.data?.length) return null;
        return RatingBands.data.find((b: any) => b.status && assessmentScore >= b.lower_bound && (assessmentScore < b.upper_bound || b.upper_bound === null)) ?? null;
    }, [RatingBands, assessmentScore]);

    useEffect(() => {
        containerRef.current?.scrollIntoView({ block: 'start' });
        const scrollableParent = containerRef.current?.closest('.p-dialog-content') as HTMLElement;
        if (scrollableParent) scrollableParent.scrollTop = 0;
    }, []);

    const [strengths, setStrengths] = useState(topStrengths ?? "");
    const [opportunity, setOpportunity] = useState(primaryCoachingOpportunity ?? "");
    const [nextSteps, setNextSteps] = useState(recommendedNextSteps ?? "");
    const [acknowledged, setAcknowledged] = useState(initialAcknowledged ?? false);
    const [tlOnCamera, setTlOnCamera] = useState<TlOnCameraValue | null>(
        (initialTlOnCameraResponse as TlOnCameraValue) ?? null
    );
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const tlOnCameraError = useMemo(
        () => isAuthor && submitAttempted && !tlOnCamera,
        [isAuthor, submitAttempted, tlOnCamera],
    );

    const handleFinish = useCallback(() => {
        if (isAuthor) {
            if (!strengths?.trim()) {
                toast.error('Top Strengths is required.');
                return;
            }
            if (!opportunity?.trim()) {
                toast.error('Primary Coaching Opportunity is required.');
                return;
            }
            if (!nextSteps?.trim()) {
                toast.error('Recommended Next Steps for TL is required.');
                return;
            }

            setSubmitAttempted(true);
            if (!tlOnCamera) {
                toast.error('Please answer the Team Lead camera question.');
                return;
            }
        }

        const hasChanges =
            acknowledged !== initialAcknowledged ||
            strengths !== (topStrengths ?? "") ||
            opportunity !== (primaryCoachingOpportunity ?? "") ||
            nextSteps !== (recommendedNextSteps ?? "") ||
            tlOnCamera !== ((initialTlOnCameraResponse as TlOnCameraValue) ?? null);

        if (!hasChanges) {
            setTriadPageNumber(0);
            return;
        }

        const payload = {
            triadId,
            assessmentScore,
            acknowledgeEmployeeNumber: loggedEmpNum,
            acknowledgeDate: new Date(),
            acknowledged,
            topStrengths: strengths,
            primaryCoachingOpportunity: opportunity,
            recommendedNextSteps: nextSteps,
            tlOnCameraResponse: tlOnCamera,
        };

        UpdateTriad(payload)
            .unwrap()
            .then((res: any) => {
                if (res.statusCode === 200) {
                    setTriadPageNumber(0);
                    toast.success('Coaching Observations Saved Successfully');
                } else {
                    toast.error('Coaching Observations Save Failed');
                }
            })
            .catch((err: any) => {
                toast.error(err);
            });
    }, [
        isAuthor, strengths, opportunity, nextSteps, tlOnCamera,
        acknowledged, initialAcknowledged, topStrengths, primaryCoachingOpportunity,
        recommendedNextSteps, initialTlOnCameraResponse, triadId, assessmentScore,
        loggedEmpNum, UpdateTriad, setTriadPageNumber,
    ]);

    const handleSupervisorReview = useCallback(() => {
        if (employeeNumber !== loggedEmpNum) {
            toast.error('Only Supervisors are authorized to acknowledge');
            return;
        }
        if (initialAcknowledged) {
            toast.info('Coaching Observations cannot be edited after Team lead has acknowledged.');
            return;
        }
        setAcknowledged((prev: boolean) => !prev);
    }, [employeeNumber, loggedEmpNum, initialAcknowledged]);

    const isCheckboxDisabled = month !== new Date().getMonth() + 1 || year !== new Date().getFullYear();

    return (
        <div ref={containerRef} style={{ margin: "0 auto", padding: "20px 16px", fontFamily: "system-ui, sans-serif", color: "#111827", fontSize: 14 }}>
            <Divider />
            {/* Top bar */}
            <div className="row mb-4" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
                <div style={{ display: "flex" }}>
                    {[
                        ["Coaching Type", coachingType ?? '--'],
                        ["Observation Method", observationMethod ?? '--'],
                        ["Session Duration (mins.)", sessionDuration ?? '00'],
                    ].map(([l, v]) => (
                        <div className="col-md-3" style={{ paddingLeft: l === 'Session Duration (mins.)' ? '15px' : undefined }} key={l}>
                            <div style={{ fontSize: 14, color: "#111827", marginBottom: 2 }}>{l}</div>
                            <div style={{ fontSize: 14, color: "#6b7280" }}>{v}</div>
                        </div>
                    ))}
                </div>
            </div>
            <Divider />
            <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "0 0 20px" }} />

            {/* Score + Bands */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
                <div style={{ padding: "18px 20px" }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginBottom: 16, textAlign: "center" }}>Final Coaching Effectiveness Score:</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textAlign: "center" }}>Total Score:</div>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 4 }}>
                        <span style={{ fontSize: 40, fontWeight: 700, color: "#111827" }}>{assessmentScore ? (assessmentScore * 100).toFixed(2) + '%' : 'Not Applicable'}</span>
                    </div>
                    {matchedBucket && (
                        <div style={{ textAlign: "center", marginTop: 8, fontSize: 14, fontWeight: 600, color: matchedBucket?.display_color }}>
                            {matchedBucket?.bucket_label}
                        </div>
                    )}
                </div>

                <div style={{ padding: "18px 20px" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 12 }}>Overall Rating Bands</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", marginBottom: 10 }}>Score Range</div>
                    {RatingBands?.data?.map((item: any) => (
                        <div key={item.triad_capability_bucket_id} style={{ display: "flex", gap: 16, marginBottom: 8, fontSize: 13 }}>
                            <span style={{ width: 70, fontWeight: 600, color: "#111827" }}>{item.range_label}</span>
                            <span style={{ color: item.display_color, fontWeight: 500 }}>{item.bucket_label}</span>
                        </div>
                    ))}
                </div>
            </div>
            <Divider />

            {/* Observer Notes */}
            <div style={{ fontSize: 17, fontWeight: 600, color: "#111827", marginBottom: 18 }}>Observer Notes</div>

            {/* Top Strengths */}
            <div style={{ marginBottom: 18 }}>
                <DescriptionBox
                    title="Top Strengths*"
                    responseDescription={strengths}
                    value={strengths}
                    limit={1000}
                    onChange={setStrengths}
                    readOnly={!isEditable}
                    placeholder="Top Strengths"
                    className={!strengths?.trim() ? 'p-invalid' : ''}
                />
            </div>

            {/* Primary Coaching Opportunity */}
            <div style={{ marginBottom: 18 }}>
                <DescriptionBox
                    title="Primary Coaching Opportunity*"
                    responseDescription={opportunity}
                    value={opportunity}
                    limit={1000}
                    onChange={setOpportunity}
                    readOnly={!isEditable}
                    placeholder="Primary Coaching Opportunity"
                    className={!opportunity?.trim() ? 'p-invalid' : ''}
                />
            </div>

            {/* Recommended Next Steps for TL */}
            <div style={{ marginBottom: 18 }}>
                <DescriptionBox
                    title="Recommended Next Steps for TL*"
                    responseDescription={nextSteps}
                    value={nextSteps}
                    limit={1000}
                    onChange={setNextSteps}
                    readOnly={!isEditable}
                    placeholder="Recommended Next Steps for TL"
                    className={!nextSteps?.trim() ? 'p-invalid' : ''}
                />
            </div>

            {/* TL On Camera — answered by Manager only, last item before acknowledge */}
            <div style={{ marginBottom: 24, padding: "20px 24px", background: "#f7f8ff", border: "1px solid #e0e4f0", borderRadius: 10 }}>
                <TlOnCameraSelect
                    label="Was the Team Lead on camera for the coaching session?"
                    value={tlOnCamera}
                    onChange={setTlOnCamera}
                    required
                    error={tlOnCameraError}
                    disabled={!isEditable}
                />
            </div>

            {/* Acknowledge */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24, fontSize: 13, color: "#374151" }}>
                <Checkbox
                    inputId="ack"
                    checked={acknowledged}
                    onChange={handleSupervisorReview}
                    disabled={isCheckboxDisabled}
                />
                <label htmlFor="ack" style={{ cursor: "pointer" }}>
                    I have reviewed the Triad with my manager and acknowledge the review.
                </label>
            </div>

            <Divider />

            {/* Footer */}
            <div className="button-container-triad" style={{ justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                <Button
                    label="Cancel"
                    icon="pi pi-times-circle"
                    className="btn-secondary"
                    onClick={() => setTriadPageNumber(0)}
                    // style={{ color: '#6366f1' }}
                />
                <Button
                    label="Return"
                    icon="pi pi-arrow-circle-left"
                    className="btn-secondary"
                    onClick={() => setTriadPageNumber(4)}
                    // style={{ color: '#6366f1' }}
                />
                <Button
                    label="Save and Close"
                    icon="pi pi-check-circle"
                    onClick={handleFinish}
                    className="btn-primary"
                    // style={{ background: '#6366f1', border: 'none' }}
                />
            </div>

        </div>
    );
};
